import { BREAKPOINTS, COLORS, Size, SystemIcons, TextButton } from "@laerdal/life-react-components";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { FormTextField } from "../../cases/components/FormTextField";
import * as yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";

const SignInContainerDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;

  ${BREAKPOINTS.LARGE}{
    grid-template-columns: repeat(2, 1fr);
  }
`;
const SignInBenefitsContainer = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${COLORS.positive_50};
    padding: 16px;
    gap: 8px;
    align-items: flex-start;
    align-self: stretch;

    ${BREAKPOINTS.LARGE}{
      padding: 32px;
      gap: 16px;
    }
`;
const SignInBenefit = styled.div`
    display: flex;
    gap: 8px;
    align-items: flex-start;
    align-self: stretch;
`;
const SignInBenefitTextContainer = styled.div`
    display: flex;
    flex-direction: column; 
    justify-content: center;
    flex: 1 0 0;
    align-self: stretch;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;

    ${BREAKPOINTS.LARGE}{
      font-size: 20px;
    }
`;
const EmphasizedText = styled.span`
    font-weight: 700;
`;
const CheckMarkIconFilledDiv = styled.div`
    padding: 4px 0;
    display: flex;
    align-items: center;
    gap: 12px;

    ${BREAKPOINTS.LARGE}{
      gap: 20px;
    }
`;
const CheckMarkIconFilled = styled(SystemIcons.CheckMark)`
    background-color: ${COLORS.positive_500};
    color: ${COLORS.white};
    display: flex;
    border-radius: 50%;
    padding: 4px;
    align-items: center;
    height: 16px;
    width: 16px;

    ${BREAKPOINTS.LARGE}{
      height: 24px;
      width: 24px;
    }
`;
const SignInFormContainer = styled.div`
    background-color: ${COLORS.positive_100};
    display: flex;
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;

    ${BREAKPOINTS.LARGE}{
      padding: 32px;
      gap: 32px;
    }
`;
const SignInForm = styled.form`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
    padding-top: 4px;

    ${BREAKPOINTS.LARGE}{
      flex: 1 0 0;
    }
`;

export interface SignInProps {
  onSignInSubmit: (emailAddress: string) => void;
}

const SignIn = ({ onSignInSubmit }: SignInProps) => {
  const { t } = useTranslation('translation');

  const SignInBenefits = [
    <span>
      {t('Create and track your')}{' '}
      <EmphasizedText>{t('support cases')}</EmphasizedText>
    </span>,
    <span>
      {t('View and track your')}{' '}
      <EmphasizedText>{t('orders and invoices')}</EmphasizedText>
    </span>,
    <span>
      {t('View product')}{' '}
      <EmphasizedText>{t('usage statistics')}</EmphasizedText>
    </span>
  ];

  const schema = yup.object().shape({
    emailAddress: yup.string().email(t('Not an email')).required(t('Required'))
  });

  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      emailAddress: '',
    }
  });

  const onSubmit = (data: { emailAddress: string }) => {
    onSignInSubmit(data.emailAddress);
  };

  return (
    <SignInContainerDiv>
      <SignInBenefitsContainer>
        {SignInBenefits.map((benefit, index) => (
          <SignInBenefit key={index}>
            <CheckMarkIconFilledDiv>
              <CheckMarkIconFilled />
            </CheckMarkIconFilledDiv>
            <SignInBenefitTextContainer>{benefit}</SignInBenefitTextContainer>
          </SignInBenefit>
        ))}
      </SignInBenefitsContainer>
      <SignInFormContainer>
        <SignInForm onSubmit={handleSubmit(onSubmit)}>
          <FormTextField
            control={control}
            errors={errors}
            label={t('Enter email address')}
            name="emailAddress"
            placeholder={t('Enter email address')}
            testId="emailAddres"
            size={Size.Medium}
          />
          <TextButton
            size={Size.Large}
            type="submit">
            {t('Sign in')}
          </TextButton>
        </SignInForm>
      </SignInFormContainer>
    </SignInContainerDiv >
  );
};

export default SignIn;